"use client"

/**
 * Drawer Navigation Context
 * Provides centralized drawer navigation across the application
 */

import { createContext, useContext, useState, useCallback, useMemo } from 'react'
import type { DrawerNavigationContextValue, DrawerType, DrawerInternalState } from './types'

// Create context with undefined default
const DrawerNavigationContext = createContext<DrawerNavigationContextValue | undefined>(undefined)

/** Hook to access drawer navigation */
export function useDrawerNavigation(): DrawerNavigationContextValue {
  const context = useContext(DrawerNavigationContext)
  if (!context) {
    throw new Error('useDrawerNavigation must be used within a DrawerNavigationProvider')
  }
  return context
}

/** Optional hook that returns null if used outside provider (for components that may be used standalone) */
export function useDrawerNavigationOptional(): DrawerNavigationContextValue | null {
  return useContext(DrawerNavigationContext) ?? null
}

interface DrawerProviderState {
  order: DrawerInternalState<unknown>
  shipment: DrawerInternalState<unknown>
  salesOrder: DrawerInternalState<unknown>
}

const initialState: DrawerProviderState = {
  order: { isOpen: false, isLoading: false, data: null },
  shipment: { isOpen: false, isLoading: false, data: null },
  salesOrder: { isOpen: false, isLoading: false, data: null },
}

interface DrawerNavigationProviderProps {
  children: React.ReactNode
  /** API functions to fetch entity data */
  apis: {
    getOrder: (id: string) => Promise<unknown>
    getShipment: (id: string) => Promise<unknown>
    getSalesOrder: (id: string) => Promise<unknown>
  }
  /** Callback when drawer data is loaded - use this to render the drawer */
  onDrawerOpen?: (type: DrawerType, data: unknown) => void
}

export function DrawerNavigationProvider({
  children,
  apis,
  onDrawerOpen,
}: DrawerNavigationProviderProps) {
  const [state, setState] = useState<DrawerProviderState>(initialState)

  const openOrderDrawer = useCallback(async (orderId: string) => {
    setState(prev => ({
      ...prev,
      order: { ...prev.order, isLoading: true },
    }))

    try {
      const data = await apis.getOrder(orderId)
      setState(prev => ({
        ...prev,
        order: { isOpen: true, isLoading: false, data },
      }))
      onDrawerOpen?.('order', data)
    } catch (error) {
      console.error('Failed to load order:', error)
      setState(prev => ({
        ...prev,
        order: { ...prev.order, isLoading: false },
      }))
    }
  }, [apis, onDrawerOpen])

  const openShipmentDrawer = useCallback(async (shipmentId: string) => {
    setState(prev => ({
      ...prev,
      shipment: { ...prev.shipment, isLoading: true },
    }))

    try {
      const data = await apis.getShipment(shipmentId)
      setState(prev => ({
        ...prev,
        shipment: { isOpen: true, isLoading: false, data },
      }))
      onDrawerOpen?.('shipment', data)
    } catch (error) {
      console.error('Failed to load shipment:', error)
      setState(prev => ({
        ...prev,
        shipment: { ...prev.shipment, isLoading: false },
      }))
    }
  }, [apis, onDrawerOpen])

  const openSalesOrderDrawer = useCallback(async (salesOrderId: string) => {
    setState(prev => ({
      ...prev,
      salesOrder: { ...prev.salesOrder, isLoading: true },
    }))

    try {
      const data = await apis.getSalesOrder(salesOrderId)
      setState(prev => ({
        ...prev,
        salesOrder: { isOpen: true, isLoading: false, data },
      }))
      onDrawerOpen?.('salesOrder', data)
    } catch (error) {
      console.error('Failed to load sales order:', error)
      setState(prev => ({
        ...prev,
        salesOrder: { ...prev.salesOrder, isLoading: false },
      }))
    }
  }, [apis, onDrawerOpen])

  const closeDrawer = useCallback((type: DrawerType) => {
    setState(prev => ({
      ...prev,
      [type]: { isOpen: false, isLoading: false, data: null },
    }))
  }, [])

  const isLoading = useCallback((type: DrawerType) => {
    return state[type].isLoading
  }, [state])

  const value = useMemo<DrawerNavigationContextValue>(() => ({
    openOrderDrawer,
    openShipmentDrawer,
    openSalesOrderDrawer,
    closeDrawer,
    isLoading,
  }), [openOrderDrawer, openShipmentDrawer, openSalesOrderDrawer, closeDrawer, isLoading])

  return (
    <DrawerNavigationContext.Provider value={value}>
      {children as any}
    </DrawerNavigationContext.Provider>
  )
}

// Export context for advanced use cases
export { DrawerNavigationContext }

// Export state getter for the container component
export function useDrawerState() {
  const [state, setState] = useState<DrawerProviderState>(initialState)
  return { state, setState }
}
